import React from 'react';
import { Row, Col, Panel } from 'react-bootstrap';
import { withRouter } from 'react-router-dom';
import ReactEcharts from 'echarts-for-react';

import { fetchApi } from '../../Network/NetworkService';

import { notif } from '../Ui/Notification';
import { CardCount } from '../Ui/Card';
import PartnerDashBoard from '../Ui/PartnerDashboard';
import RequestsTable from '../Ui/RequestsTable';
import isoDate from '../../Services/toIsoDate.js';
import { calculateTurnoverFromStats, bucketAmount } from '../../Services/pricing.js';

import '../../Assets/Ui/Dashboard.css';
import DateRangePicker from '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.js';

const STATE_LABELS = {
    incomplete: 'Incomplet',
    waiting: 'En attente',
    approved: 'Approuvé',
    toCall: 'À appeler',
    reviewed: 'Examiné',
    terminated: 'Terminé',
    canceled: 'Annulé',
    refunded: 'Remboursé',
};

const STATE_COLORS = {
    incomplete: '#ff6b6b',
    waiting: '#ffd93d',
    approved: '#6bcf7f',
    toCall: '#4dabf7',
    reviewed: '#69db7c',
    terminated: '#51cf66',
    canceled: '#fa5252',
    refunded: '#ff8787',
};

const STATE_ORDER = ['incomplete', 'waiting', 'approved', 'toCall', 'reviewed', 'terminated', 'canceled', 'refunded'];

const PERIODS = [
    { id: 'today', label: "Aujourd'hui", days: 0 },
    { id: '7d', label: '7j', days: 7 },
    { id: '30d', label: '30j', days: 30 },
    { id: '12m', label: '12m', days: 365 },
];

const PERIOD_SUBLABEL = {
    today: "Aujourd'hui",
    '7d': '7 derniers jours',
    '30d': '30 derniers jours',
    '12m': '12 derniers mois',
};

const TREND_LABEL = {
    today: 'vs hier',
    '7d': 'vs période préc.',
    '30d': 'vs période préc.',
    '12m': 'vs période préc.',
};

// Always anchor periods to the Europe/Paris calendar day, regardless of the
// admin's browser timezone — Feeli's business day is Paris.
function getParisYMD(date) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Paris',
        year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(date);
    const get = (t) => parts.find(p => p.type === t).value;
    return { y: get('year'), m: get('month'), d: get('day') };
}

function parisOffsetFor(y, m, d) {
    // Probe noon UTC: format it as Paris wall-clock, reparse as if it were UTC,
    // and the delta from the real UTC instant is the Paris offset in minutes.
    // Browser/locale-independent (no reliance on timeZoneName strings).
    const probe = new Date(`${y}-${m}-${d}T12:00:00Z`);
    const parisWall = probe.toLocaleString('sv-SE', { timeZone: 'Europe/Paris', hour12: false });
    const asIfUtc = new Date(parisWall.replace(' ', 'T') + 'Z');
    const offsetMin = Math.round((asIfUtc.getTime() - probe.getTime()) / 60000);
    const sign = offsetMin >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMin);
    return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

function getPeriodRange(period) {
    const now = new Date();
    const todayYMD = getParisYMD(now);
    const endOffset = parisOffsetFor(todayYMD.y, todayYMD.m, todayYMD.d);
    const end = new Date(`${todayYMD.y}-${todayYMD.m}-${todayYMD.d}T23:59:59.999${endOffset}`);

    const days = (PERIODS.find(p => p.id === period) || PERIODS[1]).days;
    const startProbe = new Date(end);
    startProbe.setUTCDate(startProbe.getUTCDate() - Math.max(days - 1, 0));
    const startYMD = getParisYMD(startProbe);
    const startOffset = parisOffsetFor(startYMD.y, startYMD.m, startYMD.d);
    const start = new Date(`${startYMD.y}-${startYMD.m}-${startYMD.d}T00:00:00.000${startOffset}`);

    return { start, end };
}

function formatEuro(n) {
    return `${(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;
}


const colors = {
    user: {
        main: '#8051A2',
        areaColor: '#8051A245'
    },
    request: {
        main: '#02ce26',
        areaColor: '#0084166b'
    },
    turnover: {
        main: '#D6AE71',
        areaColor: '#D6AE7150'
    },
    visit: {
        main: '#00cdac',
        areaColor: '#3ae0c4c9'
    }
};

const lineOptions = {
    tooltip: {
        trigger: 'axis'
    },
    grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
    },
    xAxis: [
        {
            type: 'category',
            boundaryGap: false,
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#878787'
                }
            },
            splitLine: {
                show: false
            },
            data: []
        }
    ],
    yAxis: [
        {
            type: 'value',
            axisLine: {
                show: false
            },
            splitLine: {
                show: false
            }
        }
    ],
    series: [
        {
            type: 'line',
            data: [],
            smooth: true,
            itemStyle: {
                normal: {
                    color: "#dedede"
                }
            },
            areaStyle: {
                color: "#dedede"
            }
        }
    ]
};

function getCurrentWeekOfMonth(date) {
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1); // Premier jour du mois
    const currentDate = date.getDate(); // Jour actuel
    const firstDayOfWeek = startOfMonth.getDay() || 7; // Jour de la semaine (1 = Lundi, 7 = Dimanche)
    return Math.ceil((currentDate + firstDayOfWeek - 1) / 7); // Calcul de la semaine
}

class Dashboard extends React.Component {
    constructor() {
        super();

        // Monotonic counter so a slow response from a previous period
        // can't overwrite state belonging to the user's current selection.
        this._kpiFetchId = 0;
        // Same guard for the response-time panel: its year selector can be
        // clicked faster than a whole-year aggregation comes back.
        this._avgTimeGapFetchId = 0;

        this.state = {
            usersCount: null,
            requests: [],
            userStats: [],
            countRequest: 0,
            doctorPercent: {},
            requestOfToday: 0,
            waitingOfToday: 0,
            userOfToday: 0,
            visitOfToday: 0,
            requestOfYesterday: 0,
            waitingOfYesterday: 0,
            userOfYesterday: 0,
            visitOfYesterday: 0,
            visitsCount: 0,
            visitStats: [],
            // Was hardcoded to '2025', with no control anywhere to change it — so
            // from January onwards the panel quietly showed the previous year.
            avgTimeGapYear: String(new Date().getFullYear()),
            avgTimeGapData: [],
            avgTimeGapLoading: true,
            avgTimeGapGlobal: '',
            requestsCA: [],
            requestsCAReviewed: [],
            turnoverOfToday: 0,
            turnoverOfYesterday: 0,
            turnoverReviewedOfToday: 0,
            turnoverReviewedOfYesterday: 0,
            turnoverPaidOfToday: 0,
            turnoverPaidOfYesterday: 0,
            countWaiting: 0,
            requestsOfTodayPartner: [],
            times: [],
            monthlyData: [],
            weeklyData: [],
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            week: getCurrentWeekOfMonth(new Date()),
            queryType: 'hour',
            range: [new Date().setDate(new Date().getDate()-1), new Date().setDate(new Date().getDate())],
            date: new Date().toISOString().split('T')[0],
            stateFlowData: {},
            stateFlowDate: new Date(),
            period: 'today',
            topDoctors: [],
            recentRequests: [],
            kpiSparks: { users: [], requests: [], waiting: [], ca: [], caValid: [], visits: [] },
        };

        this.getUsersOptions = this.getUsersOptions.bind(this);
        this.getRequestsOptions = this.getRequestsOptions.bind(this);
        this.getVisitOptions = this.getVisitOptions.bind(this);
        this.getRepOptions = this.getRepOptions.bind(this);
        this.getCAOptions = this.getCAOptions.bind(this);
        this.getCAReviewedOptions = this.getCAReviewedOptions.bind(this);
        this.getDoctorPercent = this.getDoctorPercent.bind(this);
        this.calculateTotalTurnover = this.calculateTotalTurnover.bind(this);
        this.getStateFlowOptions = this.getStateFlowOptions.bind(this);
        this.fetchStateFlowData = this.fetchStateFlowData.bind(this);
    }

  
    getUsersOptions() {

        if (!this.state.userStats) {
            return this.stats;
        }

        const options = JSON.parse(JSON.stringify(lineOptions));
        const xAxisLegend = [
            ...new Set([
                ...this.state.userStats.map(
                    ({ _id }) => _id.month.toString() + '/' + _id.year.toString()
                )
            ])
        ];
        const xAxisDataUsrs = [];

        for (let i = 0; i < xAxisLegend.length; i++) {
            const year = parseInt(xAxisLegend[i].split('/')[1]);
            const month = parseInt(xAxisLegend[i].split('/')[0]);

            const usersRegistered = this.state.userStats.find(
                ({ _id }) => _id.year === year && _id.month === month
            );

            if (!usersRegistered) {
                xAxisDataUsrs.push(0);
            } else {
                xAxisDataUsrs.push(usersRegistered.count);
            }
        }

        options.xAxis[0].data = xAxisLegend;
        options.series[0].data = xAxisDataUsrs;
        options.series[0].itemStyle.normal.color = colors.user.main;
        options.series[0].areaStyle.color = colors.user.areaColor;

        return options;
    }

    getRequestsOptions() {

        if (!this.state.requests) {
            return;
        }

        const options = JSON.parse(JSON.stringify(lineOptions));
        const xAxisLegend = [
            ...new Set([
                ...this.state.requests.map(
                    ({ _id }) => _id.month?.toString() + '/' + _id.year?.toString()
                )
            ])
        ];
        const xAxisDataRequest = {};

        this.state.requests.map(el => {

            if (!xAxisDataRequest[`${el._id.month}/${el._id.year}`]) {
                xAxisDataRequest[`${el._id.month}/${el._id.year}`] = el.count;
            } else {
                xAxisDataRequest[`${el._id.month}/${el._id.year}`] += el.count;
            }
        })

        options.xAxis[0].data = xAxisLegend;
        options.series[0].data = Object.keys(xAxisDataRequest).map(el => xAxisDataRequest[el]);
        options.series[0].itemStyle.normal.color = colors.request.main;
        options.series[0].areaStyle.color = colors.request.areaColor;

        return options;
    }

    getVisitsMonthlyOptions() {
        const options = JSON.parse(JSON.stringify(lineOptions));
        const stats = this.state.visitStats || [];
        if (stats.length === 0) return options;

        // Bucket daily visit counts by YYYY-MM, keep the last 12 months.
        const byMonth = {};
        stats.forEach(s => {
            if (!s.date) return;
            const key = String(s.date).slice(0, 7); // YYYY-MM
            byMonth[key] = (byMonth[key] || 0) + (s.count || 0);
        });
        const keys = Object.keys(byMonth).sort().slice(-12);
        const labels = keys.map(k => {
            const [y, m] = k.split('-');
            return `${parseInt(m, 10)}/${y}`;
        });

        options.xAxis[0].data = labels;
        options.series[0].data = keys.map(k => byMonth[k]);
        options.series[0].itemStyle.normal.color = colors.visit.main;
        options.series[0].areaStyle.color = colors.visit.areaColor;
        return options;
    }

    getVisitOptions() {

        const options = JSON.parse(JSON.stringify(lineOptions));

        if (!this.state.visitStats) {
            return options;
        }

        const xAxisLegend = [
            ...new Set([
                ...this.state.visitStats.map(
                    el => el.date
                )
            ])
        ];
        const xAxisDataVisits = this.state.visitStats.map(el => el.count);

        options.xAxis[0].data = xAxisLegend;
        options.series[0].data = xAxisDataVisits;
        options.series[0].itemStyle.normal.color = colors.visit.main;
        options.series[0].areaStyle.color = colors.visit.areaColor;

        return options;
    }

    getRepOptions() {
        if (!this.state.requests) {
            return;
        }
        const options = {
            title: {
              text: 'Répartition',
              left: 'left'
            },
            tooltip: {
              trigger: 'item'
            },
            series: [
              {
                name: '',
                type: 'pie',
                radius: '50%',
                data: [],
                emphasis: {
                  itemStyle: {
                    shadowBlur: 10,
                    shadowOffsetX: 0,
                    shadowColor: 'rgba(0, 0, 0, 0.5)'
                  }
                }
              }
            ]
          };

        const datas = {};
        this.state.requests.map(el => {
            
            if (!datas[el._id.type]) {
                datas[el._id.type] = {name: el._id.type, value: el.count};
            } else {
                datas[el._id.type].value += el.count;
            }
        })

        options.series[0].data = Object.keys(datas).map(el => datas[el]).filter(element=>element.value > 500);
        return JSON.parse(JSON.stringify(options));
    }

    getCAOptions() {
        const options = {
            tooltip: {
                trigger: 'axis'
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: false
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: []
            },
            yAxis: {
                type: 'value',
                axisLine: {
                    show: false
                },
                splitLine: {
                    show: false
                }
            },
            series: [
                {
                    name: "Chiffre d'affaire",
                    type: 'line',
                    smooth: true,
                    data: [],
                    itemStyle: {
                        color: colors.turnover.main
                    },
                    areaStyle: {
                        color: colors.turnover.areaColor
                    }
                }
            ]
        };

        const xAxisLegend = [
            ...new Set([
                ...this.state.requestsCA.map(
                    ({ _id }) => _id.month.toString() + '/' + _id.year.toString()
                )
            ])
        ];

        const xAxisData = [];

        for (let i = 0; i < xAxisLegend.length; i++) {
            const year = parseInt(xAxisLegend[i].split('/')[1]);
            const month = parseInt(xAxisLegend[i].split('/')[0]);

            const reqs = this.state.requestsCA.filter(
                ({ _id }) => _id.year === year && _id.month === month
            );
          
            if (!reqs) {
                xAxisData.push(0);
            } else {
                const totalAmount = reqs.reduce((acc, req) => acc + bucketAmount(req), 0);
                xAxisData.push(totalAmount);
            }
        }

        options.xAxis.data = xAxisLegend;
        options.series[0].data = xAxisData;

        return options;
    }

    getCAReviewedOptions() {
        const options = {
            tooltip: {
                trigger: 'axis'
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: false
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: []
            },
            yAxis: {
                type: 'value',
                axisLine: {
                    show: false
                },
                splitLine: {
                    show: false
                }
            },
            series: [
                {
                    name: "CA Validé",
                    type: 'line',
                    smooth: true,
                    data: [],
                    itemStyle: {
                        color: '#51cf66'
                    },
                    areaStyle: {
                        color: '#51cf6650'
                    }
                }
            ]
        };

        const xAxisLegend = [
            ...new Set([
                ...this.state.requestsCAReviewed.map(
                    ({ _id }) => _id.month.toString() + '/' + _id.year.toString()
                )
            ])
        ];

        const xAxisData = [];

        for (let i = 0; i < xAxisLegend.length; i++) {
            const year = parseInt(xAxisLegend[i].split('/')[1]);
            const month = parseInt(xAxisLegend[i].split('/')[0]);

            const reqs = this.state.requestsCAReviewed.filter(
                ({ _id }) => _id.year === year && _id.month === month
            );

            if (!reqs) {
                xAxisData.push(0);
            } else {
                const totalAmount = reqs.reduce((acc, req) => acc + bucketAmount(req), 0);
                xAxisData.push(totalAmount);
            }
        }

        options.xAxis.data = xAxisLegend;
        options.series[0].data = xAxisData;

        return options;
    }

    calculateTotalTurnover(requests) {
        const total = calculateTurnoverFromStats(requests);
        return total.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    }

    getDoctorPercent() {
            if (!this.state.doctorPercent) {
                return;
            }
        
            const options = {
                title: {
                    text: 'Docteur par pathologie'
                },
                tooltip: {
                    trigger: 'axis'
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '3%',
                    containLabel: true
                },
                xAxis: [
                    {
                        type: 'category',
                        data: [],
                        axisTick: {
                            alignWithLabel: true
                        }
                    }
                ],
                yAxis: [
                    {
                        type: 'value',
                        axisLabel: {
                            formatter: '{value} %'
                        }
                    }
                ],
                series: [
                    {
                        type: 'bar',
                        data: [],
                        barWidth: '60%',
                    }
                ]
            };
    
            options.xAxis[0].data = Object.keys(this.state.doctorPercent);
            options.series[0].data = Object.keys(this.state.doctorPercent).map(el => this.state.doctorPercent[el]);
           
            return JSON.parse(JSON.stringify(options));
    }

    getAvgTimeGap() { 

        const formattedValues = this.state.avgTimeGapData.map(value => `${Math.round(value.averageTimeGap)} mn`);

        const option = {
            tooltip: {
              trigger: "axis",
              formatter: function (params) {
                    const dataIndex = params[0].dataIndex;
                    return `${params[0].name}: ${formattedValues[dataIndex]}`;
                },
            },
            grid: {
                bottom: '150px'
            },
            xAxis: [{
                type: "category",
                data: this.state.avgTimeGapData.map(
                    ({ _id }) => _id.type || _id.year
                ),
                axisLabel: {
                    rotate: 45,
                    interval: 0
                }
            }],
            yAxis: [{
                type: "value",
                axisLabel: {
                    formatter: '{value} mn'
                },
            }],
            series: [{
              type: "bar",
              barWidth: "60%",
              data: this.state.avgTimeGapData.map(
                (el) => { 
                    return { value: el.averageTimeGap }
                }
            )
            }]
          }

        return option;

    }

    getStateFlowOptions() {
        const { stateFlowData } = this.state;
        
        if (!stateFlowData.stateCounts) {
            return {};
        }
        
        const states = ['incomplete', 'waiting', 'approved', 'toCall', 'reviewed', 'terminated', 'canceled', 'refunded'];
        const stateLabels = {
            incomplete: 'Incomplet',
            waiting: 'En attente',
            approved: 'Approuvé',
            toCall: 'À appeler',
            reviewed: 'Examiné',
            terminated: 'Terminé',
            canceled: 'Annulé',
            refunded: 'Remboursé'
        };
        
        const stateColors = {
            incomplete: '#ff6b6b',
            waiting: '#ffd93d',
            approved: '#6bcf7f',
            toCall: '#4dabf7',
            reviewed: '#69db7c',
            terminated: '#51cf66',
            canceled: '#fa5252',
            refunded: '#ff8787'
        };
        
        const options = {
            title: {
                text: `État des requêtes - ${stateFlowData.date || 'Aujourd\'hui'}`,
                left: 'center'
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: states.map(state => stateLabels[state]),
                axisLabel: {
                    rotate: 45,
                    interval: 0
                }
            },
            yAxis: {
                type: 'value',
                name: 'Nombre de requêtes'
            },
            series: [{
                name: 'Requêtes',
                type: 'bar',
                data: states.map(state => ({
                    value: stateFlowData.stateCounts[state] || 0,
                    itemStyle: {
                        color: stateColors[state]
                    }
                })),
                label: {
                    show: true,
                    position: 'top',
                    formatter: '{c}'
                }
            }]
        };
        
        return options;
    }

    getTimes() { 
        const options = {
            title: {
                text: ''
            },
            tooltip: {
                trigger: 'axis'
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            xAxis: [
                {
                    type: 'category',
                    data: [],
                    axisTick: {
                        alignWithLabel: true
                    },
                }
            ],
            yAxis: [
                {
                    type: 'value',
                    axisLabel: {
                        formatter: '{value}'
                    }
                }
            ],
            series: [
                {
                    type: 'bar',
                    data: [],
                    barWidth: '60%',
                }
            ]
        };


        if(this.state.queryType === 'hour') {
            const xAxisLegend = [
                ...new Set([
                    ...this.state.times.map(
                        ({ _id }) => _id.hour.toString() + 'h'
                    )
                ])
            ];

            const xAxisData = [
                    ...this.state.times.map(
                        (el) =>{ 
                            let color = el._id.hour >6 && el._id.hour < 18 ? "#69cfd3" : "#96edf0b6"
                            return {value: el.count, itemStyle: {color: color}}
                        }
                    )
            ];
            options.xAxis[0].data = xAxisLegend;
            options.series[0].data = xAxisData;
        }

        if(this.state.queryType === 'week') {
            const xAxisLegend = [
                ...new Set([
                    ...this.state.weeklyData.map(
                        (data) => data.day.toString()
                    )
                ])
            ];

            const xAxisData = [
                    ...this.state.weeklyData.map(
                        (el) => { 
                            let color = el.count >6 && el.count < 18 ? "#69cfd3" : "#96edf0b6"
                            return {value: el.count, itemStyle: {color: color}}
                        }
                    )
            ];
            options.xAxis[0].data = xAxisLegend;
            options.series[0].data = xAxisData;
        }
        
        if(this.state.queryType === 'month') {
            const xAxisLegend = [
                ...new Set([
                    ...this.state.monthlyData.map(
                        (data) => data.month.toString() + '/' + this.state.year
                    )
                ])
            ];

            const xAxisData = [
                    ...this.state.monthlyData.map(
                        (el) => { 
                            let color = el.month >6 && el.month < 18 ? "#69cfd3" : "#96edf0b6"
                            return {value: el.count, itemStyle: {color: color}}
                        }
                    )
            ];
            options.xAxis[0].data = xAxisLegend;
            options.series[0].data = xAxisData;
        }
       
        return JSON.parse(JSON.stringify(options));
}

    componentDidMount() {
        fetchApi('/doctors/pathology-percentage')
            .then((res) => this.setState({ doctorPercent: res.datas }))
            .catch((err) =>
                notif.addNotification(
                    'Erreur',
                    'Erreur de recuperation des donnees user: ' + err,
                    'warning'
                )
            );

        fetchApi('/users/count')
            .then(({ count }) => this.setState({ usersCount: count }))
            .catch((err) =>
                notif.addNotification(
                    'Erreur',
                    'Erreur de recuperation des donnees user: ' + err,
                    'warning'
                )
            );

        // Monthly buckets for ALL requests, whatever their state. Feeds both the
        // request charts (`requests`) and the CA Total tile (`requestsCA`) — one
        // fetch, not two. Unfiltered, this aggregation scans the whole Request
        // collection, so firing it twice per mount cost a full scan for nothing.
        fetchApi(`/requests/reqStat${this.props.role === 4 ? `?utm_source=${this.props.utm_source}` : ''}`)
            .then(({ requests }) => {
                this.setState({ requests: requests, requestsCA: requests })
            })
            .catch((err) =>
                notif.addNotification(
                    'Erreur',
                    'Erreur de recuperation des donnees request: ' + err,
                    'warning'
                )
            );

        fetchApi(`/requests/stats`)
            .then(({ cAllReq, countWaiting }) =>
                this.setState({
                    countRequest: cAllReq,
                    countWaiting
                })
            )
            .catch((err) =>
                notif.addNotification(
                    'Erreur',
                    'Erreur de recuperation des donnees request: ' + err,
                    'warning'
                )
            );

        fetchApi('/users/stats')
            .then(({ stats }) => this.setState({ userStats: stats }))
            .catch((err) =>
                notif.addNotification(
                    'Erreur',
                    'Erreur de recuperation des donnees request: ' + err,
                    'warning'
                )
            );
        
        // KPI tile counts/turnover/visits driven by the period selector.
        this.fetchKpisForPeriod(this.state.period);

        fetchApi(`/pageView/count`)
            .then((res) => {
                this.setState({visitsCount: res.count});
            })
            .catch((err) => console.log(err));
        
        fetchApi('/pageView/stats')
            .then(res =>  this.setState({ visitStats: res.stats }))
            .catch((err) =>
                notif.addNotification(
                    'Erreur',
                    'Erreur de recuperation des donnees request: ' + err,
                    'warning'
                )
            );

        // CA Total (ALL requests regardless of state) shares the `requests`
        // fetch above — same URL, same response.
        // CA reviewed only (for global chart)
        fetchApi(`/requests/reqStat?state=reviewed,terminated${this.props.role === 4 ? `&utm_source=${this.props.utm_source}` : ''}`)
            .then(({ requests }) => {
                this.setState({ requestsCAReviewed: requests })
            })
            .catch((err) =>
                notif.addNotification(
                    'Erreur',
                    'Erreur de recuperation des donnees request: ' + err,
                    'warning'
                )
            );

        if (this.props.utm_source) {
            fetchApi(`/requests/analytics?start=${isoDate(null, 0, 0)}&end=${isoDate(null, 23, 59, 59)}&utm_source=${this.props.utm_source}`)
            .then((res) => {
                this.setState({requestsOfTodayPartner: res.requests});
            })
            .catch((err) => console.log(err));
        }

        fetchApi(`/requests/stats/getTimes`, {
            method: 'POST',
            body: JSON.stringify({
                ...( { start: isoDate(this.state.range[0], 0, 0) } ),
                ...( { end: isoDate(this.state.range[1], 23, 59, 59) } ),
            })
        })
            .then((res) => {
                this.setState({times: res.stats})
            })
            .catch((err) => {
                notif.addNotification(
                    'Erreur',
                    'Erreur de recuperation des donnees utilisateurs: ' + err,
                    'warning'
                )
            });

            
        
        fetchApi(`/requests/stats/getMonthlyConsultations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                year: this.state.year
            })
        })
        .then((res) => {
            if (res.success) {
                this.setState({ monthlyData: res.stats });
                console.log(res.stats);
            } else {
                notif.addNotification(
                    'Erreur',
                    'Erreur de récupération des données : ' + res.message,
                    'warning'
                );
            }
        })
        .catch((err) => {
            notif.addNotification(
                'Erreur',
                'Erreur de récupération des données utilisateurs : ' + err,
                'warning'
            );
        });

        fetchApi(`/requests/stats/getWeeklyConsultationsInMonth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                year: this.state.year,
                month: this.state.month,
                week: this.state.week
            })
        })
        .then((res) => {
            if (res.success) {
                this.setState({ weeklyData: res.stats });
            } else {
                notif.addNotification(
                    'Erreur',
                    'Erreur de récupération des données : ' + res.message,
                    'warning'
                );
            }
        })
        .catch((err) => {
            notif.addNotification(
                'Erreur',
                'Erreur de récupération des données utilisateurs : ' + err,
                'warning'
            );
        });

        this.getAvgTimeGapData();

        // Fetch state flow data for today
        this.fetchStateFlowData(new Date());

        // New panels
        this.fetchTopDoctors();
        this.fetchRecentRequests();
    }

    getAvgTimeGapData() {
        // Pick 2019 then 2024 and the slower 2019 aggregation can land last,
        // leaving 2019's bars under a dropdown reading 2024, with no error shown.
        const fetchId = ++this._avgTimeGapFetchId;
        fetchApi(`/requests/stats/avgTimeGap`, {
            method: 'POST',
            body: JSON.stringify({
                year: this.state.avgTimeGapYear,
                isGlobal: this.state.avgTimeGapGlobal === 'global'
            })
        })
            .then((res) => {
                if (this._avgTimeGapFetchId !== fetchId) return;
                this.setState({ avgTimeGapData: res.data, avgTimeGapLoading: false })
            })
            .catch((err) => {
                notif.addNotification(
                    'Erreur',
                    'Erreur de recuperation des donnees utilisateurs: ' + err,
                    'warning'
                )
            });
    }

    fetchTopDoctors() {
        const { start, end } = getPeriodRange(this.state.period);
        fetchApi(`/doctors/top?start=${start.toISOString()}&end=${end.toISOString()}&limit=5`)
            .then((res) => {
                if (res.success) this.setState({ topDoctors: res.data });
            })
            .catch((err) => console.log('top doctors:', err));
    }

    fetchKpisForPeriod(period) {
        const { start, end } = getPeriodRange(period);
        const startISO = start.toISOString();
        const endISO = end.toISOString();
        const utm = this.props.utm_source || '';
        const utmQs = utm ? `&utm_source=${encodeURIComponent(utm)}` : '';

        // Guard against rapid period switches: stale responses are dropped.
        const fetchId = ++this._kpiFetchId;
        const apply = (updater) => (r) => {
            if (this._kpiFetchId === fetchId) this.setState(updater(r));
        };
        const onFail = (label) => (err) => console.error(`KPI fetch [${label}] failed:`, err);

        const postJson = (url, body) => fetchApi(url, {
            method: 'POST',
            body: JSON.stringify(body),
        });

        postJson('/users/count', { start: startISO, end: endISO, compareTo: 'previous' })
            .then(apply(r => ({ userOfToday: r.current || 0, userOfYesterday: r.previous || 0 })))
            .catch(onFail('utilisateurs'));

        postJson('/requests/count', {
            start: startISO, end: endISO, compareTo: 'previous',
            state: 'approved,reviewed,terminated,notOnline,waiting,toCall,refunded,canceled',
            utm_source: utm,
        })
            .then(apply(r => ({ requestOfToday: r.current || 0, requestOfYesterday: r.previous || 0 })))
            .catch(onFail('requêtes'));

        postJson('/requests/count', {
            start: startISO, end: endISO, compareTo: 'previous',
            state: 'waiting',
            utm_source: utm,
        })
            .then(apply(r => ({ waitingOfToday: r.current || 0, waitingOfYesterday: r.previous || 0 })))
            .catch(onFail('en attente'));

        fetchApi(`/requests/turnover?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}&compareTo=previous${utmQs}`)
            .then(apply(r => ({ turnoverOfToday: r.current || 0, turnoverOfYesterday: r.previous || 0 })))
            .catch(onFail('CA total'));

        fetchApi(`/requests/turnover?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}&compareTo=previous&paidOnly=true${utmQs}`)
            .then(apply(r => ({ turnoverReviewedOfToday: r.current || 0, turnoverReviewedOfYesterday: r.previous || 0 })))
            .catch(onFail('CA généré'));

        fetchApi(`/requests/turnover?start=${encodeURIComponent(startISO)}&end=${encodeURIComponent(endISO)}&compareTo=previous&paidOnly=true&state=approved,reviewed,terminated${utmQs}`)
            .then(apply(r => ({ turnoverPaidOfToday: r.current || 0, turnoverPaidOfYesterday: r.previous || 0 })))
            .catch(onFail('CA encaissé'));

        postJson('/pageView/count', { start: startISO, end: endISO, compareTo: 'previous' })
            .then(apply(r => ({ visitOfToday: r.current || 0, visitOfYesterday: r.previous || 0 })))
            .catch(onFail('visiteurs'));
    }

    fetchRecentRequests() {
        fetchApi('/requests?page=1&limit=5')
            .then((res) => {
                this.setState({ recentRequests: res.requests || [] });
            })
            .catch((err) => console.log('recent requests:', err));
    }

    onPeriodChange(periodId) {
        this.setState({ period: periodId }, () => {
            this.fetchTopDoctors();
            this.fetchKpisForPeriod(periodId);
        });
    }

    buildKpiSparks() {
        // Build small 12-month series from existing aggregates we already fetch.
        // (Server-side day-bucket aggregation would be cleaner; for now reuse monthly.)
        const monthsKey = (obj) => Object.keys(obj).sort();

        const userByMonth = {};
        (this.state.userStats || []).forEach((s) => {
            const k = `${s._id.year}-${String(s._id.month).padStart(2, '0')}`;
            userByMonth[k] = (userByMonth[k] || 0) + (s.count || 0);
        });
        const reqByMonth = {};
        const caByMonth = {};
        (this.state.requestsCA || []).forEach((s) => {
            const k = `${s._id.year}-${String(s._id.month).padStart(2, '0')}`;
            reqByMonth[k] = (reqByMonth[k] || 0) + (s.count || 0);
            caByMonth[k] = (caByMonth[k] || 0) + bucketAmount(s);
        });
        const caValidByMonth = {};
        (this.state.requestsCAReviewed || []).forEach((s) => {
            const k = `${s._id.year}-${String(s._id.month).padStart(2, '0')}`;
            caValidByMonth[k] = (caValidByMonth[k] || 0) + bucketAmount(s);
        });
        const visitByDay = {};
        (this.state.visitStats || []).forEach((v) => {
            visitByDay[v.date] = (visitByDay[v.date] || 0) + (v.count || 0);
        });
        const waitingByMonth = {};
        (this.state.requests || []).forEach((s) => {
            if (s._id && s._id.state === 'waiting') {
                const k = `${s._id.year}-${String(s._id.month).padStart(2, '0')}`;
                waitingByMonth[k] = (waitingByMonth[k] || 0) + (s.count || 0);
            }
        });

        const lastN = (obj, n) => {
            const keys = monthsKey(obj).slice(-n);
            return keys.map((k) => obj[k]);
        };

        return {
            users: lastN(userByMonth, 12),
            requests: lastN(reqByMonth, 12),
            waiting: lastN(waitingByMonth, 12),
            ca: lastN(caByMonth, 12),
            caValid: lastN(caValidByMonth, 12),
            visits: monthsKey(visitByDay).slice(-30).map((k) => visitByDay[k]),
        };
    }

    fetchStateFlowData(date) {
        const dateString = date ? date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
        fetchApi(`/requests/stats/stateFlow?date=${dateString}`)
            .then((res) => {
                if (res.success) {
                    this.setState({ stateFlowData: res });
                }
            })
            .catch((err) => console.log('Error fetching state flow data:', err));
    }

    onChangeDate() {
        if(this.state.range != null) {
            fetchApi(`/requests/stats/getTimes`, {
                method: 'POST',
                body: JSON.stringify({
                    ...( { start: isoDate(this.state.range[0], 0, 0) } ),
                    ...( { end: isoDate(this.state.range[1], 23, 59, 59) } ),
                })
            })
            .then((res) => {
                this.setState({times: res.stats})
                console.log(res.stats);
            })
            .catch((err) => {
                notif.addNotification(
                    'Erreur',
                    'Erreur de recuperation des donnees utilisateurs: ' + err,
                    'warning'
                )
            });
        }
    }
    
    fetchMonthlyData() {
        fetchApi(`/requests/stats/getMonthlyConsultations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }, 
            body: JSON.stringify({
                year: this.state.year,
            })
        })
        .then((res) => {
            if (res.success) {
                this.setState({ monthlyData: res.stats });
                console.log(res.stats);
            } else {
                notif.addNotification(
                    'Erreur',
                    'Erreur de récupération des données : ' + res.message,
                    'warning'
                );
            }
        })
        .catch((err) => {
            notif.addNotification(
                'Erreur',
                'Erreur de récupération des données utilisateurs : ' + err,
                'warning'
            );
        });
    }

    fetcWeeklyData() {
        fetchApi(`/requests/stats/getWeeklyConsultationsInMonth`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                year: this.state.year,
                month: this.state.month,
                week: this.state.week
            })
        })
        .then((res) => {
            if (res.success) {
                this.setState({ weeklyData: res.stats });
                console.log('weeklyData',res.stats);
                console.log('week',this.state.week);
                console.log('month',this.state.month);
            } else {
                notif.addNotification(
                    'Erreur',
                    'Erreur de récupération des données : ' + res.message,
                    'warning'
                );
            }
        })
        .catch((err) => {
            notif.addNotification(
                'Erreur',
                'Erreur de récupération des données utilisateurs : ' + err,
                'warning'
            );
        });
    }

    generateYears() {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear; year >= 2018; year--) {
            years.push(year);
        }
        return years;
    }

    render() {
        if (this.props.role && this.props.role < 2) {
            return (
                <div>
                    <Panel>
                        <Panel.Heading>
                            <h6>Home</h6>
                        </Panel.Heading>
                    </Panel>
                </div>
            );
        }
        const years = this.generateYears();
        return (
            
            <div className="pT-25">
                { this.props.role === 4 ? <PartnerDashBoard source={this.props.utm_source} requests={this.state.requests} requestsOfToday={this.state.requestsOfTodayPartner}/> : 
                this.props.role === 5 ? 
                    (<div className='dashboard'>
                        <Row>
                            <Col>
                                <Panel>
                                    <Panel.Heading>
                                        <h6>Global </h6>
                                    </Panel.Heading>
                                    <Panel.Body>
                                        <div className='dashboard-global'>
                                            <div className='dashboard-counter'>
                                                <div>
                                                    <p className='title'>Inscriptions</p>
                                                    <p> <i className='fa fa-user icon' style={{backgroundColor: colors.user.main}}/> {this.state.usersCount}</p>
                                                    <ReactEcharts
                                                        option={this.getUsersOptions()}
                                                        style={{ height: '250px', width: '100%' }}
                                                    />
                                                </div>
                                   
                                                <div>
                                                    <p className='title'>Requêtes</p>
                                                    <p> <i className='fa fa-file icon' style={{backgroundColor: colors.request.main}} /> {this.state.countRequest}</p>
                                                    <ReactEcharts
                                                        option={this.getRequestsOptions()}
                                                        style={{ height: '250px', width: '100%' }}
                                                    />
                                                </div>
                                    
                                                <div>
                                                    <p className='title'>CA Total</p>
                                                    <p> <i className='fa fa-euro-sign icon' style={{backgroundColor: colors.turnover.main}}/> {this.calculateTotalTurnover(this.state.requestsCA)}€</p>
                                                    <ReactEcharts
                                                        option={this.getCAOptions()}
                                                        style={{ height: '250px', width: '100%' }}
                                                    />
                                                </div>

                                                <div>
                                                    <p className='title'>CA Validé</p>
                                                    <p> <i className='fa fa-check-circle icon' style={{backgroundColor: '#51cf66'}}/> {this.calculateTotalTurnover(this.state.requestsCAReviewed)}€</p>
                                                    <ReactEcharts
                                                        option={this.getCAReviewedOptions()}
                                                        style={{ height: '250px', width: '100%' }}
                                                    />
                                                </div>

                                                <div>
                                                    <p className='title'>Visiteurs uniques</p>
                                                    <p> <i className='fa fa-eye icon' style={{backgroundColor: colors.visit.main}}/> {this.state.visitsCount}</p>
                                                    <ReactEcharts
                                                        option={this.getVisitOptions()}
                                                        style={{ height: '250px', width: '100%' }}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <Row>
                                                    <Col lg={5} md={5} sm={12} xs={12}>
                                                        <ReactEcharts
                                                            option={this.getRepOptions()}
                                                            style={{ height: '350px', width: '100%' }}
                                                        />
                                                    </Col>

                                                    <Col lg={7} md={7} sm={12} xs={12}>
                                                        <ReactEcharts
                                                            option={this.getDoctorPercent()}
                                                            style={{ height: '350px', width: '100%' }}
                                                        />
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>
                                    </Panel.Body>
                                </Panel>

                            </Col>
                        </Row>
                    </div>) :
                    this.renderStandardDashboard(years)
                    /* ORIGINAL_STANDARD_DASHBOARD_BELOW */
                }
            </div>
        );
    }

    renderStandardDashboard(years) {
        const sparks = this.buildKpiSparks();
        const stateCounts = (this.state.stateFlowData && this.state.stateFlowData.stateCounts) || {};
        const total = STATE_ORDER.reduce((a, s) => a + (stateCounts[s] || 0), 0);
        const validated = (stateCounts.approved || 0) + (stateCounts.reviewed || 0) + (stateCounts.terminated || 0);
        const validatedPct = total > 0 ? Math.round((validated / total) * 100) : 0;

        const avgPathology = (this.state.avgTimeGapData || [])
            .filter(d => d && d._id && d._id.type)
            .slice(0, 8)
            .map(d => ({ label: d._id.type, minutes: Math.round(d.averageTimeGap || 0) }));

        // Requests.jsx doesn't currently read ?state=, so we navigate without
        // the param; the page state filter is applied manually there.
        const goRequests = () => this.props.history && this.props.history.push('/requests');
        const trendLabel = TREND_LABEL[this.state.period];
        const goDoctor = (id) => this.props.history && this.props.history.push(`/doctors/detail/${id}`);

        return (
            <div className='dashboard'>
                {this.state.countWaiting > 30 && (
                    <div style={{ backgroundColor: '#dc3545', padding: '10px 20px', color: 'white', borderRadius: 8, marginBottom: 16 }}>
                        <b>Consultation en attente supérieur à 30 !</b>
                    </div>
                )}

                {/* PAGE HEAD */}
                <div className="page-head">
                    <div>
                        <h1>Accueil</h1>
                        <p className="sub">Vue d'ensemble · {PERIOD_SUBLABEL[this.state.period]}</p>
                    </div>
                    <div className="actions">
                        <div className="segmented">
                            {PERIODS.map(p => (
                                <button
                                    key={p.id}
                                    className={this.state.period === p.id ? 'active' : ''}
                                    onClick={() => this.onPeriodChange(p.id)}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ROW 1 — KPI grid */}
                <div className="kpi-grid">
                    <CardCount
                        gradient="users"
                        number={this.state.userOfToday}
                        dif={this.state.userOfToday - this.state.userOfYesterday}
                        subtitle="Inscriptions"
                        icon="zmdi zmdi-accounts-alt"
                        spark={sparks.users}
                        trendLabel={trendLabel}
                        onClick={() => this.props.history && this.props.history.push('/users')}
                    />
                    <CardCount
                        gradient="success"
                        number={this.state.requestOfToday}
                        dif={this.state.requestOfToday - this.state.requestOfYesterday}
                        subtitle="Demandes validées"
                        icon="zmdi zmdi-check"
                        spark={sparks.requests}
                        trendLabel={trendLabel}
                        onClick={() => goRequests()}
                    />
                    <CardCount
                        gradient="doctors"
                        number={this.state.waitingOfToday}
                        dif={this.state.waitingOfToday - this.state.waitingOfYesterday}
                        subtitle="En attente"
                        icon="zmdi zmdi-time"
                        spark={sparks.waiting}
                        trendLabel={trendLabel}
                        onClick={() => goRequests()}
                    />
                    <CardCount
                        gradient="revenue"
                        number={formatEuro(this.state.turnoverOfToday)}
                        dif={this.state.turnoverOfToday - this.state.turnoverOfYesterday}
                        subtitle="CA Total"
                        icon="zmdi zmdi-money"
                        spark={sparks.ca}
                        trendLabel={trendLabel}
                        onClick={() => this.props.history && this.props.history.push('/analytics/customer-value')}
                    />
                    <CardCount
                        gradient="pharma"
                        number={formatEuro(this.state.turnoverReviewedOfToday)}
                        dif={this.state.turnoverReviewedOfToday - this.state.turnoverReviewedOfYesterday}
                        subtitle="CA généré"
                        icon="zmdi zmdi-check-circle"
                        spark={sparks.caValid}
                        trendLabel={trendLabel}
                        onClick={() => this.props.history && this.props.history.push('/analytics/customer-value')}
                    />
                    <CardCount
                        gradient="revenue"
                        number={formatEuro(this.state.turnoverPaidOfToday)}
                        dif={this.state.turnoverPaidOfToday - this.state.turnoverPaidOfYesterday}
                        subtitle="CA encaissé"
                        icon="zmdi zmdi-card"
                        spark={sparks.ca}
                        trendLabel={trendLabel}
                        onClick={() => this.props.history && this.props.history.push('/analytics/customer-value')}
                    />
                    <CardCount
                        gradient="visits"
                        number={this.state.visitOfToday}
                        dif={this.state.visitOfToday - this.state.visitOfYesterday}
                        subtitle="Visiteurs uniques"
                        icon="zmdi zmdi-eye"
                        spark={sparks.visits}
                        trendLabel={trendLabel}
                    />
                </div>

                {/* ROW 2 — État des requêtes */}
                <Panel>
                    <Panel.Body>
                        <div className="panel-head">
                            <h4 className="panel-title">
                                <i className="zmdi zmdi-chart-donut" />
                                État des requêtes — aujourd'hui
                            </h4>
                            <div className="right-slot">
                                {total} requêtes · {validatedPct}% validées
                            </div>
                        </div>
                        <div className="state-stack">
                            {STATE_ORDER.map(s => {
                                const v = stateCounts[s] || 0;
                                const pct = total > 0 ? (v / total) * 100 : 0;
                                return pct > 0 ? (
                                    <div key={s} style={{ width: `${pct}%`, background: STATE_COLORS[s] }} title={`${STATE_LABELS[s]}: ${v}`} />
                                ) : null;
                            })}
                        </div>
                        <div className="state-row">
                            {STATE_ORDER.map(s => (
                                <button
                                    key={s}
                                    type="button"
                                    className="state-tile"
                                    style={{ background: STATE_COLORS[s] }}
                                    onClick={() => goRequests()}
                                >
                                    <span className="lbl">{STATE_LABELS[s]}</span>
                                    <span className="v">{(stateCounts[s] || 0).toLocaleString('fr-FR')}</span>
                                </button>
                            ))}
                        </div>
                    </Panel.Body>
                </Panel>

                {/* ROW 3 — Requêtes 12 mois · Répartition pathologie */}
                <div className="dash-row t-2-1">
                    <Panel>
                        <Panel.Body>
                            <div className="panel-head">
                                <h4 className="panel-title"><i className="zmdi zmdi-trending-up" /> Requêtes</h4>
                                <div className="right-slot">12 mois</div>
                            </div>
                            <ReactEcharts option={this.getRequestsOptions() || {}} style={{ height: 320, width: '100%' }} />
                        </Panel.Body>
                    </Panel>
                    <Panel>
                        <Panel.Body>
                            <div className="panel-head">
                                <h4 className="panel-title"><i className="zmdi zmdi-chart-donut" /> Répartition par pathologie</h4>
                            </div>
                            <ReactEcharts option={this.getRepOptions() || {}} style={{ height: 320, width: '100%' }} />
                        </Panel.Body>
                    </Panel>
                </div>

                {/* ROW 4 — Top docteurs · Temps moyen de réponse */}
                <div className="dash-row t-1-2">
                    <Panel>
                        <Panel.Body>
                            <div className="panel-head">
                                <h4 className="panel-title"><i className="zmdi zmdi-star" /> Top docteurs</h4>
                                <div className="right-slot">{PERIOD_SUBLABEL[this.state.period]}</div>
                            </div>
                            <div className="leaderboard">
                                {(this.state.topDoctors || []).map((d, i) => (
                                    <div
                                        key={d.id || i}
                                        className={`leader-row ${i < 3 ? `top-${i + 1}` : ''}`}
                                        onClick={() => goDoctor(d.id)}
                                    >
                                        <div className="rank">{i + 1}</div>
                                        <div className="meta">
                                            <div className="name">Dr {d.FName} {d.LName}</div>
                                            <div className="speciality">{d.favoriteSpecialty || '—'}</div>
                                        </div>
                                        <div className="stat">
                                            {(d.requestCount || 0).toLocaleString('fr-FR')}
                                            <span className="unit">req.</span>
                                        </div>
                                    </div>
                                ))}
                                {(!this.state.topDoctors || this.state.topDoctors.length === 0) && (
                                    <div style={{ color: 'var(--color-text-muted, #94a3b8)', textAlign: 'center', padding: 24 }}>
                                        Aucune donnée
                                    </div>
                                )}
                            </div>
                        </Panel.Body>
                    </Panel>
                    <Panel>
                        <Panel.Body>
                            <div className="panel-head">
                                <h4 className="panel-title"><i className="zmdi zmdi-time" /> Temps moyen de réponse</h4>
                                <div className="right-slot" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span>accept → ordonnance · par pathologie</span>
                                    {/* The panel covers one year and never said which. Same
                                        selector idiom as the activity chart below. */}
                                    <select
                                        value={this.state.avgTimeGapYear}
                                        onChange={(e) => this.setState({ avgTimeGapYear: e.target.value }, () => {
                                            this.getAvgTimeGapData();
                                        })}
                                        className="form-control"
                                        style={{ width: 'auto' }}
                                    >
                                        {years.map((year) => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="bar-list">
                                {avgPathology.length > 0 ? (() => {
                                    const maxMinutes = Math.max(...avgPathology.map(p => p.minutes), 1);
                                    return avgPathology.map((p) => {
                                        const pct = (p.minutes / maxMinutes) * 100;
                                        return (
                                            <div key={p.label} className="bar-row">
                                                <div className="lbl" title={p.label}>{p.label}</div>
                                                <div className="track">
                                                    <div className="fill" style={{ width: `${pct}%` }} />
                                                </div>
                                                <div className="v">{p.minutes} mn</div>
                                            </div>
                                        );
                                    });
                                })() : (
                                    <div style={{ color: 'var(--color-text-muted, #94a3b8)', textAlign: 'center', padding: 24 }}>
                                        Aucune donnée
                                    </div>
                                )}
                            </div>
                        </Panel.Body>
                    </Panel>
                </div>

                {/* ROW 5 — Utilisateurs inscrits · Visiteurs uniques */}
                <div className="dash-row t-1-1">
                    <Panel>
                        <Panel.Body>
                            <div className="panel-head">
                                <h4 className="panel-title"><i className="zmdi zmdi-accounts-alt" /> Utilisateurs inscrits</h4>
                                <div className="right-slot">12 mois</div>
                            </div>
                            <ReactEcharts option={this.getUsersOptions() || {}} style={{ height: 320, width: '100%' }} />
                        </Panel.Body>
                    </Panel>
                    <Panel>
                        <Panel.Body>
                            <div className="panel-head">
                                <h4 className="panel-title"><i className="zmdi zmdi-eye" /> Visiteurs uniques</h4>
                                <div className="right-slot">12 mois</div>
                            </div>
                            <ReactEcharts option={this.getVisitsMonthlyOptions() || {}} style={{ height: 320, width: '100%' }} />
                        </Panel.Body>
                    </Panel>
                </div>

                {/* ROW 6 — Requêtes par heure */}
                <Panel>
                    <Panel.Body>
                        <div className="panel-head">
                            <h4 className="panel-title"><i className="zmdi zmdi-time-interval" /> Requêtes par heure</h4>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <select
                                    className="form-control"
                                    style={{ width: 'auto' }}
                                    onChange={e => this.setState({ queryType: e.target.value })}
                                    value={this.state.queryType}
                                >
                                    <option value="hour">Par heure</option>
                                    <option value="week">Par semaine</option>
                                    <option value="month">Par mois</option>
                                </select>
                                {this.state.queryType === 'hour' && (
                                    <DateRangePicker
                                        onChange={value => this.setState({ range: value }, () => this.onChangeDate())}
                                        value={this.state.range}
                                        maxDate={new Date()}
                                    />
                                )}
                                {this.state.queryType === 'week' && (
                                    <select
                                        value={this.state.month}
                                        onChange={(e) => this.setState({ month: e.target.value }, this.fetcWeeklyData)}
                                        className="form-control"
                                        style={{ width: 'auto' }}
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                                            <option key={month} value={month}>
                                                {new Date(0, month - 1).toLocaleString('default', { month: 'long' })}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                {this.state.queryType === 'week' && (
                                    <select
                                        value={this.state.week}
                                        onChange={(e) => this.setState({ week: e.target.value }, () => this.fetcWeeklyData())}
                                        className="form-control"
                                        style={{ width: 'auto' }}
                                    >
                                        {Array.from({ length: 5 }, (v, k) => k + 1).map((week) => (
                                            <option key={week} value={week}>Semaine {week}</option>
                                        ))}
                                    </select>
                                )}
                                {(this.state.queryType === 'month' || this.state.queryType === 'week') && (
                                    <select
                                        value={this.state.year}
                                        onChange={(e) => this.setState({ year: e.target.value }, () => {
                                            this.state.queryType === 'week' ? this.fetcWeeklyData() : this.fetchMonthlyData();
                                        })}
                                        className="form-control"
                                        style={{ width: 'auto' }}
                                    >
                                        {years.map((year) => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>
                        <div className="hours-legend">
                            <span className="key"><span className="swatch" style={{ background: '#69cfd3' }} /> Heures de bureau</span>
                            <span className="key"><span className="swatch" style={{ background: '#96edf0b6' }} /> Hors heures de bureau</span>
                        </div>
                        <ReactEcharts option={this.getTimes()} style={{ height: 340, width: '100%' }} />
                    </Panel.Body>
                </Panel>

                {/* ROW 7 — Activité récente */}
                <Panel>
                    <Panel.Body>
                        <div className="panel-head">
                            <h4 className="panel-title"><i className="zmdi zmdi-assignment" /> Activité récente</h4>
                            <div className="right-slot">
                                <a href="#/requests" onClick={(e) => { e.preventDefault(); this.props.history && this.props.history.push('/requests'); }}>
                                    Toutes les requêtes →
                                </a>
                            </div>
                        </div>
                        {this.state.recentRequests && this.state.recentRequests.length > 0 ? (
                            <RequestsTable requests={this.state.recentRequests} history={this.props.history} user />
                        ) : (
                            <div style={{ color: 'var(--color-text-muted, #94a3b8)', textAlign: 'center', padding: 24 }}>
                                Aucune requête récente
                            </div>
                        )}
                    </Panel.Body>
                </Panel>
            </div>
        );
    }

}

export default withRouter(Dashboard);
